import React from 'react';
import { find, reduce } from 'lodash-es';
import {
    Campaign,
    CampaignStatus,
    CampaignTriggerType,
    ChannelEvent,
    DeliveryLogType,
    MessageCategory,
    RecipientEvent,
    TrackedLink,
} from '@dev/ee-api/ee-api-models';

import i18n from '@/../i18n';
import dayjs from '@/services/tools/dayjs';
import { AbSplitDisplayMode } from '@/store/campaigns/types';
import { DateDropdownOptions } from '@/services/dictionaries/DateRangeValues';
import {
    LONG_DATE_FORMAT,
    TIME_RANGE_1_DAY,
    TIME_RANGE_35_DAYS,
    TIME_RANGE_TWO_WEEKS,
    TIME_RANGE_WEEK,
} from '@/services/constants';
import { StatusBadge } from '@/assets/StatusBadge';

import { WorldMapsEnum } from './CampaignReport/Geography/CampaignReportGeographyInterface';

export interface MinimalTemplateData {
    IsWinner?: boolean;
    IsWinnerTemplate?: boolean;
    [key: string]: any;
}

export function isNewAbSplit(templates: MinimalTemplateData[]): boolean {
    if (!templates || templates.length < 2) {
        return false;
    }

    const winnerTemplate: MinimalTemplateData = find(
        templates,
        (template: MinimalTemplateData): boolean => template?.IsWinner
    );

    if (!winnerTemplate?.IsWinner) {
        return false;
    }

    return !(winnerTemplate?.IsWinner && winnerTemplate?.IsWinnerTemplate);
}

export function getWinnerTemplate(templates: MinimalTemplateData[]): MinimalTemplateData {
    return find(templates, (template: MinimalTemplateData) => template.IsWinnerTemplate);
}

export function getIsWinner(templates: MinimalTemplateData[]): MinimalTemplateData {
    return find(templates, (template: MinimalTemplateData) => template.IsWinner);
}

export function isWorldMapSelected(selectedMap: WorldMapsEnum): boolean {
    return selectedMap === WorldMapsEnum.World;
}

export const prepareAbSplitDataFromDate = (campaign: Campaign, displayMode: AbSplitDisplayMode): string => {
    if (!isNewAbSplit(campaign.CampaignTemplates)) {
        return campaign.TriggerOptions.DateToSendOn;
    }

    return displayMode === AbSplitDisplayMode.TestResult
        ? campaign.TriggerOptions.DateToSendOn
        : dayjs(campaign.TriggerOptions.DateToSendOn)
              .add(campaign.SplitOptions.DetermineWinnerAfterXMinutes, 'm')
              .format(LONG_DATE_FORMAT);
};

export const prepareAbSplitDataToDate = (campaign: Campaign, displayMode: AbSplitDisplayMode): string =>
    displayMode === AbSplitDisplayMode.TestResult
        ? dayjs(campaign.TriggerOptions.DateToSendOn)
              .add(campaign.SplitOptions.DetermineWinnerAfterXMinutes, 'm')
              .format(LONG_DATE_FORMAT)
        : dayjs().format(LONG_DATE_FORMAT);

export const sumStatFromAllLinks = (
    statName: keyof Omit<TrackedLink, 'Link' | 'Percent'>,
    linkTrackingList: TrackedLink[]
): number => reduce(linkTrackingList, (sum, singleLinkData: TrackedLink) => +singleLinkData[statName] + sum, 0);

export type MinimalEventInterface = Pick<ChannelEvent, 'Message' | 'EventType' | 'MessageCategory'>;

export const isResendAllowed = (event: MinimalEventInterface): boolean => {
    if (!event.Message) {
        return false;
    }

    if (event.EventType === DeliveryLogType.WaitingToRetry) {
        return false;
    }

    const messageCategorySubstrings = ["'Unsubscribed'", "'Complaint'", 'transactional'];

    return !(
        event.MessageCategory === 'NotDelivered' &&
        messageCategorySubstrings.some((substring) => event.Message.includes(substring))
    );
};

export function prepareErrorMessageTitle(recipient: ChannelEvent | RecipientEvent): string {
    switch (recipient.MessageCategory) {
        case MessageCategory.BlackListed:
            return i18n.t('blacklisted');
        case MessageCategory.Ignore:
            return i18n.t('ignore');
        case MessageCategory.Spam:
            return i18n.t('spam');
        case MessageCategory.NoMailbox:
            return i18n.t('nomailbox');
        case MessageCategory.GreyListed:
            return i18n.t('greylisted');
        case MessageCategory.Throttled:
            return i18n.t('throttledcategory');
        case MessageCategory.Timeout:
            return i18n.t('timeoutdesc');
        case MessageCategory.ConnectionProblem:
            return i18n.t('connectionproblem');
        case MessageCategory.SPFProblem:
            return i18n.t('spfproblem');
        case MessageCategory.AccountProblem:
            return i18n.t('accountproblem');
        case MessageCategory.DNSProblem:
            return i18n.t('dnsproblem');
        case MessageCategory.CodeError:
            return i18n.t('codeerror');
        case MessageCategory.ManualCancel:
            return i18n.t('manualcancel');
        case MessageCategory.ConnectionTerminated:
            return i18n.t('connectionterminated');
        case MessageCategory.NotDeliveredCancelled:
            return i18n.t('notdeliveredcancelled');
        case MessageCategory.NotDelivered:
            return i18n.t('notdelivered');
        case MessageCategory.Unknown:
        default:
            return '';
    }
}

export const getStatusBadge = (status: CampaignStatus, index: number): JSX.Element => (
    <StatusBadge key={`status-${index}`} value={status} status={status} borderDisabled />
);

export const getTimeRangeFromQuery = (to: string, from: string): number | string => {
    let value;
    switch (dayjs(to).diff(from, 'd')) {
        case 14:
            value = TIME_RANGE_TWO_WEEKS;
            break;
        case 1:
            value = TIME_RANGE_1_DAY;
            break;
        case 7:
            value = TIME_RANGE_WEEK;
            break;
        case 35:
            value = TIME_RANGE_35_DAYS;
            break;
        default:
            value = DateDropdownOptions.Calendar;
            break;
    }
    return value;
};

export const isFutureScheduledWithAnyDelivered = (campaign: Campaign) =>
    campaign.Status === CampaignStatus.Active &&
    campaign.TriggerOptions.SendEveryXMinutes !== 0 &&
    campaign.Stats.Delivered !== 0 &&
    campaign.TriggerOptions.Type === CampaignTriggerType.FutureScheduled;
